import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  trackWhatsAppClick,
  trackEventCustom,
} from "./utils/analytics";

/* =========================================================
   SITE CONFIG
========================================================= */

const SITE_URL = "https://hexxatech.co.in";
const PAGE_URL = `${SITE_URL}/`;

const WHATSAPP_URL =
  "https://wa.me/919226591664?text=Hello%20HexxaTech%2C%20I%20would%20like%20to%20discuss%20a%20software%20project.";

/* =========================================================
   SERVICES
========================================================= */

const services = [
  {
    icon: "⌘",
    title: "Custom Software Development",
    text: "Business software designed around your actual workflows, processes, approvals and operational requirements.",
    link: "/services/custom-software-development",
  },
  {
    icon: "▦",
    title: "ERP & Business Management",
    text: "Centralize purchasing, sales, inventory, quotations, billing, reporting and business operations in one platform.",
    link: "/services/erp-business-management",
  },
  {
    icon: "◎",
    title: "CRM & Lead Management",
    text: "Manage customers, leads, follow-ups, appointments, sales activities and business relationships from one system.",
    link: "/services/crm-lead-management",
  },
  {
    icon: "+",
    title: "Healthcare Software",
    text: "Patient, appointment, medical record, billing and operational management solutions for clinics and healthcare businesses.",
    link: "/services/healthcare-software",
  },
  {
    icon: "◇",
    title: "Inventory Management",
    text: "Track stock, purchases, sales, vendors, inward and outward transactions with real-time operational visibility.",
    link: "/services/inventory-management",
  },
  {
    icon: "⚙",
    title: "Industrial Process Software",
    text: "Digitize quotations, orders, BOMs, process vouchers, FGI, invoices, proforma invoices and payment workflows.",
    link: "/services/industrial-process-software",
  },
  {
    icon: "◈",
    title: "License Management",
    text: "Manage software licenses, clients, products, validations and real-time license information from a centralized system.",
    link: "/services/license-management",
  },
  {
    icon: "▯",
    title: "Mobile App Development",
    text: "Business-focused mobile applications with offline capabilities, data synchronization and workflow-based functionality.",
    link: "/services/mobile-app-development",
  },
];

/* =========================================================
   PROCESS
========================================================= */

const processSteps = [
  {
    number: "01",
    title: "Discovery & Requirement Analysis",
    text: "We understand your business process, users, challenges, data flow and reporting requirements before development begins.",
  },
  {
    number: "02",
    title: "Solution Planning & Architecture",
    text: "We define the software architecture, database structure, modules, integrations and technology approach.",
  },
  {
    number: "03",
    title: "UI/UX Design & Development",
    text: "We design practical interfaces and develop the system module-by-module around your business workflow.",
  },
  {
    number: "04",
    title: "Testing, Deployment & Support",
    text: "After testing and validation, we deploy the solution and provide ongoing technical support and improvements.",
  },
];

/* =========================================================
   CASE STUDIES
========================================================= */

const caseStudies = [
  {
    title: "CarTattva",
    category: "Automotive",
    description:
      "Vehicle inspection and business management platform covering customers, dealers, leads, appointments, evaluator operations and automated reporting.",
    link: "/case-studies/cartattva",
  },
  {
    title: "Swasthya",
    category: "Healthcare",
    description:
      "Healthcare management software for patient records, appointments, medical information, billing and day-to-day clinic operations.",
    link: "/case-studies/swasthya",
  },
  {
    title: "Inventory Management",
    category: "Business Operations",
    description:
      "Custom ERP and inventory solution covering purchasing, sales, vendors, stock tracking and operational reporting.",
    link: "/case-studies/inventory-management",
  },
  {
    title: "Process Control System",
    category: "Manufacturing",
    description:
      "Industrial workflow platform connecting quotation, order, inward, BOM, process vouchers, FGI, invoicing and payment receipts.",
    link: "/case-studies/process-control",
  },
  {
    title: "WiseTech License Management",
    category: "Software Management",
    description:
      "Centralized license management platform for client registration, software allocation, license validation and real-time license information.",
    link: "/case-studies/wisetech",
  },
  {
    title: "Balloon Satellite System",
    category: "Technology & Research",
    description:
      "A custom software and satellite-related solution developed for data collection, monitoring and technology research applications.",
    link: "/case-studies/satellites",
  },
];

/* =========================================================
   CAPABILITIES
========================================================= */

const capabilities = [
  "Custom ERP & CRM",
  "Business Automation",
  "Inventory Management",
  "Healthcare Software",
  "Industrial Software",
  "Mobile Applications",
  "License Management",
  "Process Monitoring",
  "Dashboard & Reporting",
  "API & System Integration",
];

/* =========================================================
   FAQ
========================================================= */

const faqs = [
  {
    question: "What does HexxaTech Softwares develop?",
    answer:
      "HexxaTech Softwares develops custom ERP, CRM, healthcare, inventory, industrial process control, license management, business automation and mobile application solutions.",
  },
  {
    question: "Does HexxaTech provide custom software development in Pune?",
    answer:
      "Yes. HexxaTech Softwares provides custom software development services for businesses in Pune, including organizations in Hadapsar, Magarpatta and other parts of the city, as well as clients across India.",
  },
  {
    question: "Can HexxaTech build software around an existing business process?",
    answer:
      "Yes. HexxaTech follows a business-first development approach and can design software around existing workflows, approvals, reports, users, databases and operational requirements.",
  },
  {
    question: "What industries does HexxaTech work with?",
    answer:
      "HexxaTech develops software for businesses across healthcare, automotive, manufacturing, business operations, software management, education and technology-related applications.",
  },
  {
    question: "Does HexxaTech provide ongoing software support?",
    answer:
      "Yes. HexxaTech can provide ongoing maintenance, technical support, improvements, enhancements and further development after software deployment.",
  },
];

/* =========================================================
   COMPONENT
========================================================= */

export default function HexxaTechLandingPage() {
  useEffect(() => {
    /* =====================================================
       BASIC SEO
    ===================================================== */

    document.title =
      "HexxaTech Softwares | Custom Software Development Company in Pune";

    const description =
      "HexxaTech Softwares is a custom software development company in Pune building ERP, CRM, healthcare, inventory, industrial process control, license management and mobile applications for businesses across India.";

    const setNameMeta = (name, content) => {
      let element = document.querySelector(`meta[name="${name}"]`);

      if (!element) {
        element = document.createElement("meta");
        element.setAttribute("name", name);
        document.head.appendChild(element);
      }

      element.setAttribute("content", content);
    };

    const setPropertyMeta = (property, content) => {
      let element = document.querySelector(
        `meta[property="${property}"]`
      );

      if (!element) {
        element = document.createElement("meta");
        element.setAttribute("property", property);
        document.head.appendChild(element);
      }

      element.setAttribute("content", content);
    };

    setNameMeta("description", description);

    setNameMeta(
      "robots",
      "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
    );

    setNameMeta(
      "keywords",
      "custom software development company Pune, software development company Pune, custom software development Hadapsar, ERP software development Pune, CRM development Pune, healthcare software Pune, inventory management software Pune, industrial software development Pune, mobile app development Pune"
    );

    setNameMeta(
      "author",
      "HexxaTech Softwares"
    );

    /* =====================================================
       CANONICAL
    ===================================================== */

    let canonical = document.querySelector(
      'link[rel="canonical"]'
    );

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }

    canonical.href = PAGE_URL;

    /* =====================================================
       OPEN GRAPH
    ===================================================== */

    setPropertyMeta(
      "og:title",
      "HexxaTech Softwares | Custom Software Development Company in Pune"
    );

    setPropertyMeta(
      "og:description",
      "Custom ERP, CRM, healthcare, inventory, industrial process control and business automation software built around real business workflows."
    );

    setPropertyMeta("og:type", "website");
    setPropertyMeta("og:url", PAGE_URL);
    setPropertyMeta("og:site_name", "HexxaTech Softwares");
    setPropertyMeta("og:locale", "en_IN");

    /* =====================================================
       TWITTER
    ===================================================== */

    setNameMeta(
      "twitter:card",
      "summary_large_image"
    );

    setNameMeta(
      "twitter:title",
      "HexxaTech Softwares | Custom Software Development Company in Pune"
    );

    setNameMeta(
      "twitter:description",
      "Custom software, ERP, CRM, healthcare, inventory and business automation solutions from Pune."
    );

    /* =====================================================
       ORGANIZATION SCHEMA
    ===================================================== */

    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: "HexxaTech Softwares",
      url: SITE_URL,
      description:
        "Custom software development company providing ERP, CRM, healthcare, inventory, industrial process control, license management and mobile application solutions.",
      areaServed: [
        {
          "@type": "City",
          name: "Pune",
        },
        {
          "@type": "Country",
          name: "India",
        },
      ],
      serviceType: [
        "Custom Software Development",
        "ERP Development",
        "CRM Development",
        "Healthcare Software Development",
        "Inventory Management Software",
        "Industrial Software Development",
        "Mobile App Development",
        "License Management Software",
        "Business Automation",
      ],
    };

    const organizationScriptId =
      "hexxatech-organization-schema";

    let organizationScript = document.getElementById(
      organizationScriptId
    );

    if (!organizationScript) {
      organizationScript = document.createElement("script");
      organizationScript.id = organizationScriptId;
      organizationScript.type = "application/ld+json";
      document.head.appendChild(organizationScript);
    }

    organizationScript.textContent = JSON.stringify(
      organizationSchema
    );

    /* =====================================================
       LOCAL BUSINESS SCHEMA
    ===================================================== */

    const localBusinessSchema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `${SITE_URL}/#localbusiness`,
      name: "HexxaTech Softwares",
      url: SITE_URL,
      description:
        "Custom software development company in Pune providing ERP, CRM, healthcare, inventory, industrial and business automation software.",
      priceRange: "$$",
      areaServed: [
        {
          "@type": "City",
          name: "Pune",
        },
        {
          "@type": "Country",
          name: "India",
        },
      ],
      knowsAbout: [
        "Custom Software Development",
        "ERP Software",
        "CRM Software",
        "Healthcare Software",
        "Inventory Management",
        "Industrial Process Control",
        "Mobile App Development",
        "Business Automation",
      ],
    };

    const localBusinessScriptId =
      "hexxatech-local-business-schema";

    let localBusinessScript = document.getElementById(
      localBusinessScriptId
    );

    if (!localBusinessScript) {
      localBusinessScript = document.createElement("script");
      localBusinessScript.id = localBusinessScriptId;
      localBusinessScript.type = "application/ld+json";
      document.head.appendChild(localBusinessScript);
    }

    localBusinessScript.textContent = JSON.stringify(
      localBusinessSchema
    );

    /* =====================================================
       WEBSITE SCHEMA
    ===================================================== */

    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      url: SITE_URL,
      name: "HexxaTech Softwares",
      description:
        "Custom software development company in Pune.",
      publisher: {
        "@id": `${SITE_URL}/#organization`,
      },
    };

    const websiteScriptId =
      "hexxatech-website-schema";

    let websiteScript = document.getElementById(
      websiteScriptId
    );

    if (!websiteScript) {
      websiteScript = document.createElement("script");
      websiteScript.id = websiteScriptId;
      websiteScript.type = "application/ld+json";
      document.head.appendChild(websiteScript);
    }

    websiteScript.textContent = JSON.stringify(
      websiteSchema
    );

    /* =====================================================
       FAQ SCHEMA
    ===================================================== */

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    };

    const faqScriptId =
      "hexxatech-faq-schema";

    let faqScript = document.getElementById(
      faqScriptId
    );

    if (!faqScript) {
      faqScript = document.createElement("script");
      faqScript.id = faqScriptId;
      faqScript.type = "application/ld+json";
      document.head.appendChild(faqScript);
    }

    faqScript.textContent = JSON.stringify(
      faqSchema
    );
  }, []);

  return (
    <div className="hexxa-home">

      <style>{`
        * {
          box-sizing: border-box;
        }

        html {
          scroll-behavior: smooth;
        }

        body {
          margin: 0;
          font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI",
            Roboto, Helvetica, Arial, sans-serif;
          color: #0f172a;
          background: #ffffff;
        }

        .hexxa-home {
          overflow: hidden;
          background: #ffffff;
        }

        .hexxa-container {
          width: min(1180px, calc(100% - 40px));
          margin: 0 auto;
        }

        /* =========================
           HERO
        ========================= */

        .hexxa-hero {
          position: relative;
          background:
            radial-gradient(circle at 80% 20%, rgba(59,130,246,.18), transparent 30%),
            linear-gradient(135deg, #294a7d 0%, #355b91 55%, #294a7d 100%);
          color: #ffffff;
          padding: 105px 0 85px;
        }

        .hexxa-hero::before {
          content: "";
          position: absolute;
          width: 420px;
          height: 420px;
          border-radius: 50%;
          background: rgba(255,255,255,.035);
          top: -220px;
          right: -100px;
        }

        .hexxa-hero-grid {
          position: relative;
          z-index: 1;
          display: grid;
          grid-template-columns: 1.05fr .95fr;
          gap: 70px;
          align-items: center;
        }

        .hexxa-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 7px 12px;
          border-radius: 999px;
          background: rgba(255,255,255,.10);
          border: 1px solid rgba(255,255,255,.16);
          color: #dbeafe;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 1.2px;
          text-transform: uppercase;
          margin-bottom: 20px;
        }

        .hexxa-eyebrow-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #60a5fa;
        }

        .hexxa-hero h1 {
          margin: 0;
          max-width: 720px;
          font-size: clamp(42px, 5vw, 68px);
          line-height: 1.02;
          letter-spacing: -2.8px;
          font-weight: 800;
        }

        .hexxa-hero h1 span {
          color: #60a5fa;
        }

        .hexxa-hero-text {
          max-width: 650px;
          margin: 25px 0 30px;
          color: #dbeafe;
          font-size: 17px;
          line-height: 1.8;
        }

        .hexxa-buttons {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .hexxa-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 48px;
          padding: 0 20px;
          border-radius: 10px;
          text-decoration: none;
          font-size: 14px;
          font-weight: 700;
          transition: .25s ease;
        }

        .hexxa-btn-primary {
          background: #2563eb;
          color: #ffffff;
          box-shadow: 0 12px 25px rgba(15,23,42,.20);
        }

        .hexxa-btn-primary:hover {
          transform: translateY(-2px);
          background: #1d4ed8;
        }

        .hexxa-btn-light {
          background: rgba(255,255,255,.08);
          color: #ffffff;
          border: 1px solid rgba(255,255,255,.22);
        }

        .hexxa-btn-light:hover {
          background: rgba(255,255,255,.15);
        }

        .hexxa-hero-visual {
          position: relative;
          min-height: 360px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .hexxa-dashboard {
          position: relative;
          width: min(470px, 100%);
          padding: 16px;
          border-radius: 22px;
          background: rgba(255,255,255,.10);
          border: 1px solid rgba(255,255,255,.18);
          backdrop-filter: blur(10px);
          box-shadow: 0 30px 70px rgba(15,23,42,.20);
          transform: rotate(-2deg);
        }

        .hexxa-dashboard-top {
          display: flex;
          gap: 6px;
          padding-bottom: 14px;
        }

        .hexxa-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: rgba(255,255,255,.6);
        }

        .hexxa-dashboard-body {
          display: grid;
          grid-template-columns: 80px 1fr;
          gap: 12px;
        }

        .hexxa-sidebar {
          border-radius: 12px;
          background: rgba(255,255,255,.10);
          min-height: 230px;
          padding: 12px 8px;
        }

        .hexxa-side-line {
          height: 8px;
          border-radius: 5px;
          background: rgba(255,255,255,.20);
          margin-bottom: 12px;
        }

        .hexxa-side-line.active {
          background: #60a5fa;
        }

        .hexxa-main-panel {
          display: grid;
          gap: 12px;
        }

        .hexxa-mini-cards {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
        }

        .hexxa-mini-card {
          padding: 14px;
          min-height: 75px;
          border-radius: 12px;
          background: rgba(255,255,255,.11);
          border: 1px solid rgba(255,255,255,.10);
        }

        .hexxa-mini-card strong {
          display: block;
          font-size: 20px;
          margin-bottom: 5px;
        }

        .hexxa-mini-card small {
          color: #cbd5e1;
          font-size: 9px;
        }

        .hexxa-chart {
          min-height: 140px;
          border-radius: 14px;
          background: rgba(255,255,255,.09);
          border: 1px solid rgba(255,255,255,.10);
          display: flex;
          align-items: flex-end;
          gap: 9px;
          padding: 20px;
        }

        .hexxa-bar {
          flex: 1;
          border-radius: 5px 5px 2px 2px;
          background: linear-gradient(to top, #2563eb, #60a5fa);
        }

        .hexxa-bar:nth-child(1) { height: 35%; }
        .hexxa-bar:nth-child(2) { height: 55%; }
        .hexxa-bar:nth-child(3) { height: 45%; }
        .hexxa-bar:nth-child(4) { height: 72%; }
        .hexxa-bar:nth-child(5) { height: 62%; }
        .hexxa-bar:nth-child(6) { height: 88%; }
        .hexxa-bar:nth-child(7) { height: 76%; }

        /* =========================
           STATS
        ========================= */

        .hexxa-stats-wrap {
          margin-top: -38px;
          position: relative;
          z-index: 3;
        }

        .hexxa-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 20px;
          box-shadow: 0 18px 50px rgba(15,23,42,.08);
          overflow: hidden;
        }

        .hexxa-stat {
          padding: 30px;
          text-align: center;
          border-right: 1px solid #e5e7eb;
        }

        .hexxa-stat:last-child {
          border-right: 0;
        }

        .hexxa-stat strong {
          display: block;
          font-size: 34px;
          color: #2563eb;
          letter-spacing: -1px;
        }

        .hexxa-stat span {
          display: block;
          margin-top: 5px;
          color: #64748b;
          font-size: 13px;
        }

        /* =========================
           COMMON SECTION
        ========================= */

        .hexxa-section {
          padding: 90px 0;
        }

        .hexxa-section-soft {
          background: #f8fafc;
        }

        .hexxa-section-label {
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1.5px;
          text-transform: uppercase;
        }

        .hexxa-section-title {
          margin: 12px 0 0;
          max-width: 750px;
          font-size: clamp(32px, 4vw, 48px);
          line-height: 1.1;
          letter-spacing: -1.8px;
          color: #0f172a;
        }

        .hexxa-section-description {
          margin: 18px 0 0;
          max-width: 700px;
          color: #64748b;
          font-size: 16px;
          line-height: 1.8;
        }

        /* =========================
           ABOUT
        ========================= */

        .hexxa-about-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 70px;
          align-items: center;
        }

        .hexxa-about-text {
          color: #64748b;
          font-size: 15px;
          line-height: 1.85;
          margin-top: 22px;
        }

        .hexxa-about-points {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
          margin-top: 28px;
        }

        .hexxa-point {
          display: flex;
          gap: 10px;
        }

        .hexxa-check {
          flex-shrink: 0;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: #eff6ff;
          color: #2563eb;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 800;
        }

        .hexxa-point strong {
          display: block;
          font-size: 13px;
          margin-bottom: 3px;
          color: #0f172a;
        }

        .hexxa-point span {
          display: block;
          color: #64748b;
          font-size: 12px;
          line-height: 1.5;
        }

        .hexxa-build-box {
          padding: 32px;
          border-radius: 22px;
          border: 1px solid #e5e7eb;
          background: #ffffff;
          box-shadow: 0 20px 50px rgba(15,23,42,.06);
        }

        .hexxa-build-box h3 {
          margin: 0 0 22px;
          font-size: 20px;
        }

        .hexxa-build-list {
          display: grid;
          gap: 12px;
        }

        .hexxa-build-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          border-radius: 10px;
          background: #f8fafc;
          color: #475569;
          font-size: 13px;
        }

        .hexxa-build-item span {
          color: #2563eb;
          font-weight: 800;
        }

        /* =========================
           SERVICES
        ========================= */

        .hexxa-services-header {
          text-align: center;
          max-width: 780px;
          margin: 0 auto 45px;
        }

        .hexxa-services-header .hexxa-section-title {
          margin-left: auto;
          margin-right: auto;
        }

        .hexxa-services-header .hexxa-section-description {
          margin-left: auto;
          margin-right: auto;
        }

        .hexxa-service-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }

        .hexxa-service {
          display: block;
          padding: 25px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          min-height: 215px;
          transition: .3s ease;
          cursor: pointer;
          text-decoration: none;
          color: inherit;
        }

        .hexxa-service:hover {
          transform: translateY(-5px);
          border-color: #bfdbfe;
          box-shadow: 0 18px 40px rgba(15,23,42,.07);
        }

        .hexxa-service-icon {
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 11px;
          background: #eff6ff;
          color: #2563eb;
          font-weight: 800;
          font-size: 19px;
          margin-bottom: 20px;
        }

        .hexxa-service h3 {
          margin: 0 0 9px;
          font-size: 16px;
          color: #0f172a;
        }

        .hexxa-service p {
          margin: 0;
          color: #64748b;
          font-size: 12px;
          line-height: 1.7;
        }

        /* =========================
           PROCESS
        ========================= */

        .hexxa-process-grid {
          display: grid;
          grid-template-columns: .9fr 1.1fr;
          gap: 70px;
          align-items: start;
        }

        .hexxa-process-list {
          margin-top: 35px;
          display: grid;
          gap: 10px;
        }

        .hexxa-process-item {
          padding: 20px;
          border: 1px solid #e5e7eb;
          background: #ffffff;
          border-radius: 14px;
          transition: .25s ease;
        }

        .hexxa-process-item:hover {
          border-color: #bfdbfe;
          transform: translateX(4px);
        }

        .hexxa-process-number {
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1px;
        }

        .hexxa-process-item h3 {
          margin: 7px 0;
          font-size: 15px;
        }

        .hexxa-process-item p {
          margin: 0;
          color: #64748b;
          font-size: 12px;
          line-height: 1.7;
        }

        .hexxa-process-visual {
          position: sticky;
          top: 100px;
          padding: 35px;
          border-radius: 24px;
          background: linear-gradient(145deg, #eff6ff, #ffffff);
          border: 1px solid #dbeafe;
        }

        .hexxa-flow {
          display: grid;
          gap: 10px;
        }

        .hexxa-flow-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
        }

        .hexxa-flow-icon {
          width: 30px;
          height: 30px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #eff6ff;
          color: #2563eb;
          font-size: 12px;
          font-weight: 800;
        }

        .hexxa-flow-row strong {
          font-size: 12px;
        }

        /* =========================
           CASE STUDIES
        ========================= */

        .hexxa-case-header {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 30px;
          margin-bottom: 35px;
        }

        .hexxa-case-header p {
          max-width: 420px;
          margin: 0;
          color: #64748b;
          font-size: 14px;
          line-height: 1.7;
        }

        .hexxa-case-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }

        .hexxa-case-card {
          display: flex;
          flex-direction: column;
          min-height: 310px;
          padding: 26px;
          border-radius: 18px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          text-decoration: none;
          color: inherit;
          transition: .3s ease;
        }

        .hexxa-case-card:hover {
          transform: translateY(-6px);
          border-color: #bfdbfe;
          box-shadow: 0 20px 45px rgba(15,23,42,.08);
        }

        .hexxa-case-category {
          display: inline-flex;
          align-self: flex-start;
          padding: 6px 9px;
          border-radius: 999px;
          background: #eff6ff;
          color: #2563eb;
          font-size: 9px;
          font-weight: 800;
          letter-spacing: .8px;
          text-transform: uppercase;
        }

        .hexxa-case-card h3 {
          margin: 20px 0 8px;
          font-size: 20px;
        }

        .hexxa-case-card p {
          margin: 0;
          color: #64748b;
          font-size: 13px;
          line-height: 1.7;
        }

        .hexxa-case-link {
          margin-top: auto;
          padding-top: 25px;
          color: #2563eb;
          font-size: 12px;
          font-weight: 800;
        }

        /* =========================
           CAPABILITIES
        ========================= */

        .hexxa-capabilities {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 35px;
        }

        .hexxa-capability {
          padding: 11px 14px;
          border-radius: 10px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          color: #475569;
          font-size: 12px;
          font-weight: 600;
        }

        /* =========================
           FAQ
        ========================= */

        .hexxa-faq-header {
          max-width: 760px;
          margin-bottom: 35px;
        }

        .hexxa-faq-list {
          display: grid;
          gap: 12px;
          max-width: 900px;
        }

        .hexxa-faq-item {
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          background: #ffffff;
          overflow: hidden;
        }

        .hexxa-faq-item summary {
          cursor: pointer;
          list-style: none;
          padding: 20px 22px;
          font-size: 15px;
          font-weight: 700;
          color: #0f172a;
        }

        .hexxa-faq-item summary::-webkit-details-marker {
          display: none;
        }

        .hexxa-faq-item summary::after {
          content: "+";
          float: right;
          color: #2563eb;
          font-size: 20px;
          line-height: 1;
        }

        .hexxa-faq-item[open] summary::after {
          content: "−";
        }

        .hexxa-faq-answer {
          padding: 0 22px 20px;
          color: #64748b;
          font-size: 13px;
          line-height: 1.8;
        }

        /* =========================
           CTA
        ========================= */

        .hexxa-cta {
          padding: 85px 0;
        }

        .hexxa-cta-box {
          position: relative;
          overflow: hidden;
          padding: 70px 30px;
          text-align: center;
          border-radius: 24px;
          color: #ffffff;
          background:
            radial-gradient(circle at 85% 15%, rgba(59,130,246,.30), transparent 30%),
            linear-gradient(135deg, #0f172a, #172554);
        }

        .hexxa-cta-box::before {
          content: "";
          position: absolute;
          width: 300px;
          height: 300px;
          border-radius: 50%;
          border: 1px solid rgba(255,255,255,.08);
          top: -170px;
          left: -80px;
        }

        .hexxa-cta-box > * {
          position: relative;
          z-index: 1;
        }

        .hexxa-cta-label {
          display: inline-block;
          padding: 7px 12px;
          border-radius: 999px;
          background: rgba(255,255,255,.08);
          border: 1px solid rgba(255,255,255,.10);
          color: #bfdbfe;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        .hexxa-cta h2 {
          max-width: 700px;
          margin: 20px auto 12px;
          font-size: clamp(34px, 4vw, 50px);
          line-height: 1.1;
          letter-spacing: -1.5px;
        }

        .hexxa-cta p {
          max-width: 650px;
          margin: 0 auto 28px;
          color: #cbd5e1;
          font-size: 15px;
          line-height: 1.8;
        }

        .hexxa-cta .hexxa-btn-primary {
          background: #ffffff;
          color: #0f172a;
        }

        .hexxa-cta .hexxa-btn-primary:hover {
          background: #eff6ff;
        }

        /* =========================
           SEO CONTENT
        ========================= */

        .hexxa-seo-content {
          padding: 0 0 70px;
          background: #ffffff;
        }

        .hexxa-seo-content-inner {
          max-width: 900px;
        }

        .hexxa-seo-content h2 {
          font-size: 24px;
          margin: 0 0 14px;
          color: #0f172a;
        }

        .hexxa-seo-content h3 {
          font-size: 20px;
          margin: 30px 0 10px;
          color: #0f172a;
        }

        .hexxa-seo-content p {
          color: #64748b;
          font-size: 14px;
          line-height: 1.8;
          margin: 0 0 15px;
        }

        /* =========================
           RESPONSIVE
        ========================= */

        @media (max-width: 1000px) {
          .hexxa-service-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .hexxa-case-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .hexxa-hero-grid {
            grid-template-columns: 1fr;
            gap: 40px;
          }

          .hexxa-hero-visual {
            max-width: 600px;
          }

          .hexxa-about-grid,
          .hexxa-process-grid {
            grid-template-columns: 1fr;
            gap: 45px;
          }

          .hexxa-process-visual {
            position: static;
          }
        }

        @media (max-width: 700px) {
          .hexxa-container {
            width: min(100% - 28px, 1180px);
          }

          .hexxa-hero {
            padding: 75px 0 65px;
          }

          .hexxa-hero h1 {
            font-size: 42px;
            letter-spacing: -1.8px;
          }

          .hexxa-hero-text {
            font-size: 15px;
          }

          .hexxa-dashboard {
            transform: none;
          }

          .hexxa-stats {
            grid-template-columns: 1fr;
          }

          .hexxa-stat {
            border-right: 0;
            border-bottom: 1px solid #e5e7eb;
          }

          .hexxa-stat:last-child {
            border-bottom: 0;
          }

          .hexxa-section {
            padding: 65px 0;
          }

          .hexxa-about-points {
            grid-template-columns: 1fr;
          }

          .hexxa-service-grid,
          .hexxa-case-grid {
            grid-template-columns: 1fr;
          }

          .hexxa-case-header {
            display: block;
          }

          .hexxa-case-header p {
            margin-top: 15px;
          }

          .hexxa-process-visual {
            padding: 20px;
          }

          .hexxa-mini-cards {
            grid-template-columns: 1fr;
          }

          .hexxa-dashboard-body {
            grid-template-columns: 55px 1fr;
          }

          .hexxa-sidebar {
            min-height: 190px;
          }

          .hexxa-cta {
            padding: 65px 0;
          }

          .hexxa-cta-box {
            padding: 50px 20px;
            border-radius: 18px;
          }
        }
      `}</style>

      {/* =====================================================
          HERO
      ===================================================== */}

      <section className="hexxa-hero">
        <div className="hexxa-container hexxa-hero-grid">

          <div>
            <div className="hexxa-eyebrow">
              <span
                className="hexxa-eyebrow-dot"
                aria-hidden="true"
              ></span>

              Custom Software Development
            </div>

            <h1>
              Custom software built around
              <br />
              <span>the way your business works.</span>
            </h1>

            <p className="hexxa-hero-text">
              HexxaTech Softwares is a custom software development company
              in Pune building ERP, CRM, healthcare, inventory, industrial
              process, license management and mobile applications around
              real-world business workflows.
            </p>

            <div className="hexxa-buttons">

              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hexxa-btn hexxa-btn-primary"
                aria-label="Discuss your software project with HexxaTech on WhatsApp"
                onClick={() =>
                  trackWhatsAppClick("hero_cta")
                }
              >
                Discuss Your Project →
              </a>

              <Link
                to="/case-studies"
                className="hexxa-btn hexxa-btn-light"
                onClick={() =>
                  trackEventCustom(
                    "case_studies_click",
                    {
                      location: "hero",
                    }
                  )
                }
              >
                View Case Studies →
              </Link>

            </div>
          </div>

          <div
            className="hexxa-hero-visual"
            aria-hidden="true"
          >
            <div className="hexxa-dashboard">

              <div className="hexxa-dashboard-top">
                <span className="hexxa-dot"></span>
                <span className="hexxa-dot"></span>
                <span className="hexxa-dot"></span>
              </div>

              <div className="hexxa-dashboard-body">

                <div className="hexxa-sidebar">
                  <div className="hexxa-side-line active"></div>
                  <div className="hexxa-side-line"></div>
                  <div className="hexxa-side-line"></div>
                  <div className="hexxa-side-line"></div>
                  <div className="hexxa-side-line"></div>
                </div>

                <div className="hexxa-main-panel">

                  <div className="hexxa-mini-cards">

                    <div className="hexxa-mini-card">
                      <strong>ERP</strong>
                      <small>Business Operations</small>
                    </div>

                    <div className="hexxa-mini-card">
                      <strong>CRM</strong>
                      <small>Customer Management</small>
                    </div>

                    <div className="hexxa-mini-card">
                      <strong>24/7</strong>
                      <small>Business Visibility</small>
                    </div>

                  </div>

                  <div className="hexxa-chart">
                    <div className="hexxa-bar"></div>
                    <div className="hexxa-bar"></div>
                    <div className="hexxa-bar"></div>
                    <div className="hexxa-bar"></div>
                    <div className="hexxa-bar"></div>
                    <div className="hexxa-bar"></div>
                    <div className="hexxa-bar"></div>
                  </div>

                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* =====================================================
          STATS
      ===================================================== */}

      <section className="hexxa-stats-wrap" aria-label="HexxaTech statistics">
        <div className="hexxa-container">

          <div className="hexxa-stats">

            <div className="hexxa-stat">
              <strong>20+</strong>
              <span>Projects Delivered</span>
            </div>

            <div className="hexxa-stat">
              <strong>10+</strong>
              <span>Industries Served</span>
            </div>

            <div className="hexxa-stat">
              <strong>100%</strong>
              <span>Custom Built Solutions</span>
            </div>

          </div>

        </div>
      </section>

      {/* =====================================================
          ABOUT
      ===================================================== */}

      <section
        id="about"
        className="hexxa-section"
      >
        <div className="hexxa-container hexxa-about-grid">

          <div>

            <span className="hexxa-section-label">
              About HexxaTech
            </span>

            <h2 className="hexxa-section-title">
              We turn business processes into practical software.
            </h2>

            <p className="hexxa-about-text">
              HexxaTech Softwares is a custom software development company
              based in Pune, focused on building practical digital solutions
              for businesses. We work closely with organizations to
              understand how their teams operate and then convert those
              workflows into reliable software.
            </p>

            <p className="hexxa-about-text">
              From <Link to="/services/erp-business-management">ERP & Business Management</Link> and <Link to="/services/crm-lead-management">CRM & Lead Management</Link> systems to <Link to="/services/healthcare-software">healthcare applications</Link>, <Link to="/services/inventory-management">inventory platforms</Link>, <Link to="/services/industrial-process-software">industrial process management</Link>, <Link to="/services/license-management">license management</Link> and <Link to="/services/mobile-app-development">mobile applications</Link>, our approach is simple: understand the business first, then build the technology around it.
            </p>

            <div className="hexxa-about-points">

              <div className="hexxa-point">
                <div className="hexxa-check">✓</div>

                <div>
                  <strong>Business-focused</strong>
                  <span>
                    Solutions designed around actual workflows.
                  </span>
                </div>
              </div>

              <div className="hexxa-point">
                <div className="hexxa-check">✓</div>

                <div>
                  <strong>Long-term support</strong>
                  <span>
                    Ongoing maintenance and technical assistance.
                  </span>
                </div>
              </div>

              <div className="hexxa-point">
                <div className="hexxa-check">✓</div>

                <div>
                  <strong>Scalable architecture</strong>
                  <span>
                    Built to grow as your business grows.
                  </span>
                </div>
              </div>

              <div className="hexxa-point">
                <div className="hexxa-check">✓</div>

                <div>
                  <strong>Custom development</strong>
                  <span>
                    No unnecessary features or rigid templates.
                  </span>
                </div>
              </div>

            </div>

          </div>

          <div className="hexxa-build-box">

            <h3>What we build</h3>

            <div className="hexxa-build-list">

              {[
                "ERP & Business Management Software",
                "CRM & Lead Management Systems",
                "Healthcare & Clinic Management Software",
                "Inventory & Stock Management Systems",
                "Industrial Process Control Applications",
                "License Management Platforms",
                "Mobile Applications & Offline Apps",
                "Dashboards, Reports & Business Automation",
              ].map((item) => (
                <div
                  className="hexxa-build-item"
                  key={item}
                >
                  <span>✓</span>
                  {item}
                </div>
              ))}

            </div>

            <div style={{ marginTop: "25px" }}>

              <a
                href="#services"
                className="hexxa-btn hexxa-btn-primary"
                onClick={() =>
                  trackEventCustom(
                    "services_section_click",
                    {
                      location: "about",
                    }
                  )
                }
              >
                Explore Our Services →
              </a>

            </div>

          </div>

        </div>
      </section>

      {/* =====================================================
          SERVICES
      ===================================================== */}

      <section
        id="services"
        className="hexxa-section hexxa-section-soft"
      >
        <div className="hexxa-container">

          <div className="hexxa-services-header">

            <span className="hexxa-section-label">
              Our Services
            </span>

            <h2 className="hexxa-section-title">
              Custom software solutions for growing businesses.
            </h2>

            <p className="hexxa-section-description">
              We design and develop software that helps businesses automate
              repetitive work, centralize information, improve operational
              visibility and make better decisions.
            </p>

          </div>

          <div className="hexxa-service-grid">

            {services.map((service) => (
              <Link
                to={service.link}
                className="hexxa-service"
                key={service.title}
                onClick={() =>
                  trackEventCustom(
                    "service_click",
                    {
                      service: service.title,
                      location: "homepage_services",
                    }
                  )
                }
              >

                <div
                  className="hexxa-service-icon"
                  aria-hidden="true"
                >
                  {service.icon}
                </div>

                <h3>{service.title}</h3>

                <p>{service.text}</p>

              </Link>
            ))}

          </div>

        </div>
      </section>

      {/* =====================================================
          HOW IT WORKS
      ===================================================== */}

      <section
        id="how-it-works"
        className="hexxa-section"
      >
        <div className="hexxa-container">

          <div className="hexxa-process-grid">

            <div>

              <span className="hexxa-section-label">
                Our Development Process
              </span>

              <h2 className="hexxa-section-title">
                From business idea to working software.
              </h2>

              <p className="hexxa-section-description">
                Our structured software development process helps turn
                business requirements into reliable, scalable and
                maintainable digital solutions.
              </p>

              <div className="hexxa-process-list">

                {processSteps.map((step) => (
                  <div
                    className="hexxa-process-item"
                    key={step.number}
                  >

                    <div className="hexxa-process-number">
                      {step.number}
                    </div>

                    <h3>{step.title}</h3>

                    <p>{step.text}</p>

                  </div>
                ))}

              </div>

            </div>

            <div className="hexxa-process-visual">

              <span className="hexxa-section-label">
                Business Workflow
              </span>

              <h3
                style={{
                  margin: "12px 0 25px",
                  fontSize: "23px",
                }}
              >
                Technology that follows your process.
              </h3>

              <div className="hexxa-flow">

                {[
                  "Business Requirements",
                  "System Architecture",
                  "Software Development",
                  "Testing & Validation",
                  "Deployment & Support",
                ].map((item, index) => (
                  <div
                    className="hexxa-flow-row"
                    key={item}
                  >

                    <div className="hexxa-flow-icon">
                      {String(index + 1).padStart(2, "0")}
                    </div>

                    <strong>{item}</strong>

                  </div>
                ))}

              </div>

              <p
                style={{
                  margin: "22px 0 0",
                  color: "#64748b",
                  fontSize: "13px",
                  lineHeight: 1.7,
                }}
              >
                Whether it is a small business application or a complex
                multi-module platform, we build the system around the way
                your organization works.
              </p>

            </div>

          </div>

        </div>
      </section>

      {/* =====================================================
          CASE STUDIES
      ===================================================== */}

      <section className="hexxa-section hexxa-section-soft">

        <div className="hexxa-container">

          <div className="hexxa-case-header">

            <div>

              <span className="hexxa-section-label">
                Selected Work
              </span>

              <h2 className="hexxa-section-title">
                Software built around the way businesses work.
              </h2>

            </div>

            <p>
              Explore real-world software projects developed for automotive,
              healthcare, manufacturing, software management, business
              operations and technology research.
            </p>

          </div>

          <div className="hexxa-case-grid">

            {caseStudies.map((study) => (
              <Link
                key={study.title}
                to={study.link}
                className="hexxa-case-card"
                onClick={() =>
                  trackEventCustom(
                    "case_study_click",
                    {
                      case_study: study.title,
                      category: study.category,
                      location: "homepage_case_studies",
                    }
                  )
                }
              >

                <span className="hexxa-case-category">
                  {study.category}
                </span>

                <h3>{study.title}</h3>

                <p>{study.description}</p>

                <div className="hexxa-case-link">
                  View {study.title} case study →
                </div>

              </Link>
            ))}

          </div>

          <div
            style={{
              textAlign: "center",
              marginTop: "35px",
            }}
          >

            <Link
              to="/case-studies"
              className="hexxa-btn hexxa-btn-primary"
              onClick={() =>
                trackEventCustom(
                  "case_studies_all_click",
                  {
                    location: "case_studies_section",
                  }
                )
              }
            >
              Explore All Case Studies →
            </Link>

          </div>

        </div>

      </section>

      {/* =====================================================
          CAPABILITIES
      ===================================================== */}

      <section className="hexxa-section">

        <div className="hexxa-container">

          <span className="hexxa-section-label">
            Technology & Capabilities
          </span>

          <h2 className="hexxa-section-title">
            Built for practical business outcomes.
          </h2>

          <p className="hexxa-section-description">
            Our solutions combine business logic, database-driven
            applications, dashboards, automation, integrations and mobile
            experiences to solve real operational problems.
          </p>

          <div className="hexxa-capabilities">

            {capabilities.map((capability) => (
              <span
                className="hexxa-capability"
                key={capability}
              >
                {capability}
              </span>
            ))}

          </div>

        </div>

      </section>

      {/* =====================================================
          FAQ
      ===================================================== */}

      <section
        className="hexxa-section hexxa-section-soft"
        id="faq"
      >

        <div className="hexxa-container">

          <div className="hexxa-faq-header">

            <span className="hexxa-section-label">
              Frequently Asked Questions
            </span>

            <h2 className="hexxa-section-title">
              Questions about custom software development.
            </h2>

            <p className="hexxa-section-description">
              Learn more about HexxaTech's custom software development
              services, development approach, industries and ongoing support.
            </p>

          </div>

          <div className="hexxa-faq-list">

            {faqs.map((faq) => (
              <details
                className="hexxa-faq-item"
                key={faq.question}
              >

                <summary>
                  {faq.question}
                </summary>

                <div className="hexxa-faq-answer">
                  {faq.answer}
                </div>

              </details>
            ))}

          </div>

        </div>

      </section>

      {/* =====================================================
          CONTACT CTA
      ===================================================== */}

      <section
        id="contact"
        className="hexxa-cta"
      >

        <div className="hexxa-container">

          <div className="hexxa-cta-box">

            <span className="hexxa-cta-label">
              Start Your Project
            </span>

            <h2>
              Have a business problem that software can solve?
            </h2>

            <p>
              Looking for software built around your business workflow? Explore our <Link to="/services/custom-software-development">custom software development services</Link> and tell us about your workflow, challenges and goals. We can help turn your requirements into a practical, scalable software solution built specifically for your business.
            </p>

            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hexxa-btn hexxa-btn-primary"
              aria-label="Discuss your software project with HexxaTech on WhatsApp"
              onClick={() =>
                trackWhatsAppClick("contact_cta")
              }
            >
              Discuss Your Project →
            </a>

          </div>

        </div>

      </section>

      {/* =====================================================
          SEO CONTENT
      ===================================================== */}

      <section className="hexxa-seo-content">

        <div className="hexxa-container hexxa-seo-content-inner">

          <h2>
            Custom Software Development Company in Pune
          </h2>

          <p>
            HexxaTech Softwares is a custom software development company in
            Pune helping businesses improve operational efficiency through
            practical technology solutions. We develop ERP software, CRM
            systems, inventory management software, healthcare management
            systems, industrial process control applications, license
            management platforms, business dashboards and mobile
            applications.
          </p>

          <p>
            Our development approach starts with understanding how a
            business operates. Instead of forcing organizations to change
            their processes to match generic software, we build applications
            around their workflows, users, approvals, reports, integrations
            and operational requirements.
          </p>

          <p>
            Businesses in Pune and across India can work with HexxaTech for
            new software development, modernization of existing applications,
            business automation, database-driven systems, API integrations,
            dashboards and mobile applications.
          </p>

          <h3>
            Custom Software Development in Pune & Hadapsar
          </h3>

          <p>
            HexxaTech serves businesses across Pune, including Hadapsar,
            Magarpatta and surrounding business areas. Our solutions are
            designed for organizations that need software tailored to their
            specific operational processes rather than a one-size-fits-all
            product.
          </p>

          <h3>
            Business Software Designed Around Your Workflow
          </h3>

          <p>
            From customer management and sales follow-ups to inventory,
            purchasing, healthcare operations, industrial processes,
            reporting and automation, our objective is to convert complex
            business workflows into simple and maintainable software
            systems.
          </p>

        </div>

      </section>

    </div>
  );
}